// Flow-field pathfinding.
//
// A single Dijkstra pass from the goal tile produces a distance field over the
// whole walkable map; the gradient of that field gives every unit its own
// smooth direction vector without per-unit A*. This is what keeps a 40-unit
// attack-move from clumping or deadlocking: units sharing a destination share
// one field, spread naturally through chokes, and always have a valid gradient
// to follow even when shoved off their intended tile.
//
// Fields are cached by (goal tile, grid version). Placing or losing a building
// bumps grid.version and invalidates the whole cache -- a single new footprint
// can change the route to every goal on the map, so there is nothing cheaper to
// invalidate than everything.
//
// PERFORMANCE NOTES (a 96x96 field is 9216 tiles / ~73k edge relaxations):
//   * The priority queue is a pair of preallocated typed arrays with manual
//     swaps. The previous version swapped with array destructuring
//     (`[a[i],a[j]] = [a[j],a[i]]`), which allocates two throwaway arrays per
//     swap -- that alone was ~70% of the 3.3 ms build cost.
//   * Direction vectors are stored as two Int8Arrays (component * 127) instead
//     of Float32Arrays: 18 KB per cached field instead of 74 KB, and the LRU
//     holds 48 of them.
//   * The gradient is a downhill-weighted sum over the passable neighbours
//     rather than "snap to the single best neighbour". Best-neighbour snapping
//     quantises steering to 8 directions, which is what makes units oscillate
//     between two tiles on a ridge where two routes tie.
//   * Passability and the obstacle-proximity penalty are hoisted out of the
//     inner loop and recomputed once per grid version, lazily, so five
//     buildings finishing in one tick cost one refresh rather than five.

import { FLOWFIELD_CACHE } from './constants.js';

// --- integer cost model ---------------------------------------------------
// Costs are integers in 1/64ths of a tile so the queue can be a bucket queue
// (Dial's algorithm) instead of a binary heap: every push and pop is O(1) and
// there is no float comparison anywhere in the hot loop. It also removes a
// whole class of bug -- a float64 heap key that does not compare equal to the
// float32 value stored in the distance array lets a node be re-expanded
// forever, which turned one field build on this map into an infinite loop.
const SCALE = 64;
const C_ORTHO = SCALE;                        // 1.0 tile
const C_DIAG = Math.round(Math.SQRT2 * SCALE);// 1.414 tiles
const C_PEN = Math.round(0.22 * SCALE);       // per blocked neighbour
const DANGER_MAX = 16;                        // tiles, clamp on the optional danger layer
const NBUCKET = C_DIAG + 8 * C_PEN + DANGER_MAX * SCALE + 2;
export const FIELD_INF = 0x7fffffff;

const S = Math.SQRT1_2;
// 4 orthogonal first, then 4 diagonal -- the k >= 4 test is the diagonal test.
const NDX = new Int32Array([1, -1, 0, 0, 1, 1, -1, -1]);
const NDY = new Int32Array([0, 0, 1, -1, 1, -1, 1, -1]);
const NCOST = new Int32Array([C_ORTHO, C_ORTHO, C_ORTHO, C_ORTHO, C_DIAG, C_DIAG, C_DIAG, C_DIAG]);
const NUX = new Float64Array([1, -1, 0, 0, S, S, -S, -S]);
const NUY = new Float64Array([0, 0, 1, -1, S, -S, S, -S]);

// ---- bucket queue, all module-level and reused across builds -------------
const _bhead = new Int32Array(NBUCKET);
let _eNode = new Int32Array(1 << 16);
let _eNext = new Int32Array(1 << 16);
let _eN = 0;
let _maxSeen = 0;

function qReset() {
  _bhead.fill(-1);
  _eN = 0;
  _maxSeen = 0;
}
function qPush(d, node) {
  if (_eN === _eNode.length) {
    const a = new Int32Array(_eN * 2); a.set(_eNode); _eNode = a;
    const b = new Int32Array(_eN * 2); b.set(_eNext); _eNext = b;
  }
  const i = _eN++;
  const b = d % NBUCKET;
  _eNode[i] = node; _eNext[i] = _bhead[b]; _bhead[b] = i;
  if (d > _maxSeen) _maxSeen = d;
}

export class FlowFieldCache {
  constructor(grid) {
    this.grid = grid;
    this.map = new Map();      // key -> field
    // Eviction is FIFO, not LRU: a hit deliberately does not re-order the
    // queue. Touching an array of 48 on every one of a few hundred cache hits
    // per tick costs more than the occasional early eviction of a hot goal,
    // and a goal that is still hot is rebuilt in under a millisecond.
    this.order = [];
    this.version = -1;
    this.dirty = true;
    this.stats = { builds: 0, hits: 0 };
    this.pass = null;
    this.pen = null;
    // Optional per-tile extra cost in tiles (Float32Array), for routing an army
    // around a known threat. Nothing sets it today; the Dijkstra loop reads it
    // through a null check so leaving it unset costs one predicted branch.
    this._danger = null;
  }

  /**
   * Mark the cache stale. Deliberately does NOT do the O(tiles) passability
   * rebake -- several buildings can finish in one tick and each one calls this.
   * The rebake happens on the next field request instead.
   */
  invalidate() {
    this.map.clear();
    this.order.length = 0;
    this.dirty = true;
  }

  _refresh() {
    const g = this.grid, w = g.w, h = g.h, n = w * h;
    let pass = this.pass;
    if (!pass || pass.length !== n) pass = this.pass = new Uint8Array(n);
    const cliff = g.cliff, occ = g.occ;
    for (let i = 0; i < n; i++) pass[i] = (cliff[i] === 0 && occ[i] === -1) ? 1 : 0;
    for (let x = 0; x < w; x++) { pass[x] = 0; pass[(h - 1) * w + x] = 0; }
    for (let y = 0; y < h; y++) { pass[y * w] = 0; pass[y * w + w - 1] = 0; }

    // Integer, so the Dijkstra inner loop stays in Smis end to end -- a single
    // Float32Array read in there turns every distance into a double and the
    // whole loop deoptimises.
    let pen = this.pen;
    if (!pen || pen.length !== n) pen = this.pen = new Int32Array(n);
    else pen.fill(0);
    // Bias routes away from walls so groups funnel down the middle of a ramp
    // instead of grinding along both faces of it.
    for (let y = 1; y < h - 1; y++) {
      const row = y * w;
      for (let x = 1; x < w - 1; x++) {
        const i = row + x;
        if (pass[i] === 0) continue;
        const blocked = (8
          - pass[i - w - 1] - pass[i - w] - pass[i - w + 1]
          - pass[i - 1] - pass[i + 1]
          - pass[i + w - 1] - pass[i + w] - pass[i + w + 1]);
        if (blocked !== 0) pen[i] = blocked * C_PEN;
      }
    }
    this.version = g.version;
    this.dirty = false;
  }

  _sync() {
    if (this.dirty || this.grid.version !== this.version) {
      if (this.grid.version !== this.version) { this.map.clear(); this.order.length = 0; }
      this._refresh();
    }
  }

  /** Cached field for this goal tile, or null. Never builds. */
  peek(gx, gy) {
    this._sync();
    const f = this.map.get(((gy | 0) * this.grid.w + (gx | 0)));
    if (f) this.stats.hits++;
    return f || null;
  }

  /** Field for the given goal tile, built on demand. */
  get(gx, gy) {
    this._sync();
    gx |= 0; gy |= 0;
    const key = gy * this.grid.w + gx;
    const hit = this.map.get(key);
    if (hit) { this.stats.hits++; return hit; }
    const f = this.build(gx, gy);
    this.map.set(key, f);
    this.order.push(key);
    while (this.order.length > FLOWFIELD_CACHE) this.map.delete(this.order.shift());
    return f;
  }

  build(gx, gy) {
    this.stats.builds++;
    this._sync();
    const grid = this.grid;
    const pass = this.pass, pen = this.pen;
    const w = grid.w, h = grid.h, n = w * h;
    const dist = new Int32Array(n).fill(FIELD_INF);
    const fx = new Int8Array(n);
    const fy = new Int8Array(n);

    qReset();

    // If the goal itself is blocked, seed from every passable tile touching it
    // so units still converge on the nearest legal standing spot.
    if (gx >= 0 && gy >= 0 && gx < w && gy < h && pass[gy * w + gx]) {
      const s0 = gy * w + gx;
      dist[s0] = 0; qPush(0, s0);
    } else {
      for (let r = 1; r <= 10; r++) {
        let found = false;
        for (let dy = -r; dy <= r; dy++) {
          const ay = dy < 0 ? -dy : dy;
          for (let dx = -r; dx <= r; dx++) {
            if (ay !== r && (dx < 0 ? -dx : dx) !== r) continue;
            const nx = gx + dx, ny = gy + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            const si = ny * w + nx;
            if (!pass[si] || dist[si] === 0) continue;
            dist[si] = 0; qPush(0, si); found = true;
          }
        }
        if (found) break;
      }
    }

    // Precomputed flat neighbour offsets: the inner loop never multiplies.
    const OFF = this._off || (this._off = new Int32Array(8));
    if (this._offW !== w) { for (let k = 0; k < 8; k++) OFF[k] = NDY[k] * w + NDX[k]; this._offW = w; }

    const danger = this._danger;
    for (let d = 0; d <= _maxSeen; d++) {
      const b = d % NBUCKET;
      let i = _bhead[b];
      if (i === -1) continue;
      _bhead[b] = -1;
      while (i !== -1) {
        const cur = _eNode[i];
        i = _eNext[i];
        if (dist[cur] !== d) continue;            // stale entry
        const cy = (cur / w) | 0;
        const cx = cur - cy * w;
        for (let k = 0; k < 8; k++) {
          const nx = cx + NDX[k], ny = cy + NDY[k];
          if (nx < 1 || ny < 1 || nx >= w - 1 || ny >= h - 1) continue;
          const ni = cur + OFF[k];
          if (pass[ni] === 0) continue;
          // no corner cutting: a diagonal step needs both orthogonal tiles open
          if (k >= 4 && (pass[cur + NDX[k]] === 0 || pass[cur + NDY[k] * w] === 0)) continue;
          let nd = d + NCOST[k] + pen[ni];
          if (danger !== null) {
            let dg = danger[ni];
            if (dg > DANGER_MAX) dg = DANGER_MAX; else if (!(dg > 0)) dg = 0;
            nd += (dg * SCALE) | 0;
          }
          if (nd < dist[ni]) { dist[ni] = nd; qPush(nd, ni); }
        }
      }
    }

    // Direction = normalised downhill-weighted sum over the passable
    // neighbours. Using the weighted sum rather than "step to the single
    // cheapest neighbour" keeps steering continuous across a ridge where two
    // routes cost the same, which is where 8-way snapping makes units
    // oscillate between two tiles forever.
    for (let y = 1; y < h - 1; y++) {
      const row = y * w;
      for (let x = 1; x < w - 1; x++) {
        const i = row + x;
        const di = dist[i];
        if (di === FIELD_INF) continue;
        let ax = 0, ay = 0, bd = di, bk = -1;
        for (let k = 0; k < 8; k++) {
          const ni = i + OFF[k];
          if (pass[ni] === 0) continue;
          const dn = dist[ni];
          if (dn === FIELD_INF) continue;
          if (k >= 4 && (pass[i + NDX[k]] === 0 || pass[i + NDY[k] * w] === 0)) continue;
          const dd = di - dn;
          if (dd > 0) { ax += NUX[k] * dd; ay += NUY[k] * dd; }
          if (dn < bd) { bd = dn; bk = k; }
        }
        let vx, vy;
        const m2 = ax * ax + ay * ay;
        if (m2 > 1e-9) { const m = Math.sqrt(m2); vx = ax / m; vy = ay / m; }
        else if (bk >= 0) { vx = NUX[bk]; vy = NUY[bk]; }
        else continue;                            // goal tile: zero vector
        fx[i] = (vx * 127 + (vx < 0 ? -0.5 : 0.5)) | 0;
        fy[i] = (vy * 127 + (vy < 0 ? -0.5 : 0.5)) | 0;
      }
    }
    return { gx, gy, dist, fx, fy, w, h, scale: SCALE };
  }
}

/** Sample a field's direction at a float position, bilinearly blended. */
export function sampleFlow(field, x, y, out) {
  const w = field.w, h = field.h;
  const fxp = x - 0.5, fyp = y - 0.5;
  let x0 = Math.floor(fxp), y0 = Math.floor(fyp);
  const tx = fxp - x0, ty = fyp - y0;
  if (x0 < 0) x0 = 0; else if (x0 > w - 2) x0 = w - 2;
  if (y0 < 0) y0 = 0; else if (y0 > h - 2) y0 = h - 2;
  const FX = field.fx, FY = field.fy, D = field.dist;
  const i0 = y0 * w + x0, i1 = i0 + w;
  const w00 = (1 - tx) * (1 - ty), w10 = tx * (1 - ty);
  const w01 = (1 - tx) * ty, w11 = tx * ty;
  let vx = 0, vy = 0, ws = 0;
  if (D[i0] !== FIELD_INF) { vx += FX[i0] * w00; vy += FY[i0] * w00; ws += w00; }
  if (D[i0 + 1] !== FIELD_INF) { vx += FX[i0 + 1] * w10; vy += FY[i0 + 1] * w10; ws += w10; }
  if (D[i1] !== FIELD_INF) { vx += FX[i1] * w01; vy += FY[i1] * w01; ws += w01; }
  if (D[i1 + 1] !== FIELD_INF) { vx += FX[i1 + 1] * w11; vy += FY[i1 + 1] * w11; ws += w11; }
  if (ws < 1e-4) {
    // fully inside an unreachable pocket; fall back to the raw cell
    let xi = Math.round(x - 0.5), yi = Math.round(y - 0.5);
    if (xi < 0) xi = 0; else if (xi > w - 1) xi = w - 1;
    if (yi < 0) yi = 0; else if (yi > h - 1) yi = h - 1;
    const idx = yi * w + xi;
    out.x = FX[idx] / 127; out.y = FY[idx] / 127;
    return out;
  }
  const len = Math.sqrt(vx * vx + vy * vy);
  if (len < 1e-6) { out.x = 0; out.y = 0; return out; }
  out.x = vx / len; out.y = vy / len;
  return out;
}

/** Path distance IN TILES from a tile to the field goal, Infinity if unreachable. */
export function fieldDist(field, x, y) {
  const xi = Math.max(0, Math.min(field.w - 1, x | 0));
  const yi = Math.max(0, Math.min(field.h - 1, y | 0));
  const d = field.dist[yi * field.w + xi];
  return d === FIELD_INF ? Infinity : d / SCALE;
}

/** True if the goal of `field` is reachable on foot from tile (x,y). */
export function fieldReachable(field, x, y) {
  const xi = Math.max(0, Math.min(field.w - 1, x | 0));
  const yi = Math.max(0, Math.min(field.h - 1, y | 0));
  return field.dist[yi * field.w + xi] !== FIELD_INF;
}

/**
 * True if a disc of radius `r` can slide along the straight segment
 * (x0,y0)->(x1,y1) without touching unwalkable terrain.
 *
 * This is the cheap escape hatch that keeps flow-field builds rare: a unit
 * chasing a target 6 tiles away across open ground does not need a Dijkstra
 * pass, and a chased target that moves every tick would otherwise force a
 * fresh field every tick. Short trips (mining, dropoff, closing on a target)
 * are the overwhelming majority of movement in a match.
 */
export function lineClear(grid, x0, y0, x1, y1, r) {
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.sqrt(dx * dx + dy * dy);
  const steps = Math.ceil(len / 0.45) + 1;
  const sx = dx / steps, sy = dy / steps;
  const g = r * 0.72;
  let px = x0, py = y0;
  for (let s = 0; s <= steps; s++) {
    const tx0 = Math.floor(px - g), tx1 = Math.floor(px + g);
    const ty0 = Math.floor(py - g), ty1 = Math.floor(py + g);
    for (let ty = ty0; ty <= ty1; ty++)
      for (let tx = tx0; tx <= tx1; tx++)
        if (!grid.passable(tx, ty)) return false;
    px += sx; py += sy;
  }
  return true;
}
